import React, { useState, useEffect, useCallback } from 'react';
import monitoringService from '../services/monitoringService';

const Deployments = () => {
  const [deployments, setDeployments] = useState([]);
  const [repos, setRepos] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggerLoading, setTriggerLoading] = useState(null);

  const loadDeployments = useCallback(async () => {
    setLoading(true);
    try {
      const result = await monitoringService.getDeployHistory(selectedRepo, 30);
      if (result.success) {
        setDeployments(result.data.deployments || []);
        setRepos(result.data.repos || []);
      }
    } catch (err) {
      console.error('Deploy history error:', err);
    }
    setLoading(false);
  }, [selectedRepo]);

  useEffect(() => { loadDeployments(); }, [loadDeployments]);

  const handleTriggerDeploy = async (repoName) => {
    if (!window.confirm(`${repoName}을(를) 배포하시겠습니까?`)) return;
    setTriggerLoading(repoName);
    try {
      await monitoringService.triggerDeploy(repoName);
      alert('배포가 트리거되었습니다.');
      setTimeout(loadDeployments, 3000);
    } catch (err) {
      alert('배포 트리거 실패: ' + err.message);
    }
    setTriggerLoading(null);
  };

  const getStatusBadge = (status) => {
    const map = {
      success: { bg: 'bg-green-100', text: 'text-green-700', label: '성공' },
      completed: { bg: 'bg-green-100', text: 'text-green-700', label: '성공' },
      failure: { bg: 'bg-red-100', text: 'text-red-700', label: '실패' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', label: '취소' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', label: '진행중' },
      queued: { bg: 'bg-amber-100', text: 'text-amber-700', label: '대기중' }
    };
    const s = map[status] || { bg: 'bg-gray-100', text: 'text-gray-600', label: status || '알 수 없음' };
    return <span className={`inline-flex items-center py-1 px-2.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>{s.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        <p className="text-gray-600 text-sm">배포 이력 로딩 중...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-[1200px] mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-[28px] font-bold text-gray-900 m-0">배포 관리</h1>
        <button onClick={loadDeployments} className="text-sm text-blue-500 hover:text-blue-700 bg-blue-50 px-4 py-2 rounded-lg">새로고침</button>
      </div>

      {/* Repo filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedRepo(null)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            !selectedRepo ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >전체</button>
        {repos.map(repo => (
          <button
            key={repo}
            onClick={() => setSelectedRepo(repo)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              selectedRepo === repo ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >{repo.replace('ilouli-', '')}</button>
        ))}
      </div>

      {/* Deployment list */}
      {deployments.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <p className="text-lg mb-2">배포 이력이 없습니다</p>
          <p className="text-sm">GITHUB_TOKEN을 설정하면 GitHub Actions 배포 이력을 볼 수 있습니다.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-600">프로젝트</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">워크플로우</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">커밋</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">상태</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">소요시간</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">시간</th>
              </tr>
            </thead>
            <tbody>
              {deployments.map((dep, i) => (
                <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <span className="font-medium text-gray-900">{dep.project.replace('ilouli-', '')}</span>
                  </td>
                  <td className="py-3 px-4 text-gray-600">{dep.workflow}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-col">
                      <code className="text-xs text-blue-600 font-mono">{dep.commit}</code>
                      <span className="text-xs text-gray-500 truncate max-w-[200px]">{dep.commitMessage}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">{getStatusBadge(dep.status)}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{dep.duration ? `${dep.duration}s` : '-'}</td>
                  <td className="py-3 px-4 text-right text-gray-500 text-xs">
                    {dep.startedAt ? new Date(dep.startedAt).toLocaleString('ko-KR') : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Deployments;
